// Chat related types
export type ChatStatus = 'active' | 'resolved' | 'pending';

export enum CustomerSupportStatus {
	AI_RESPONSES = 'AI_RESPONSES',
	NEEDS_REVIEW = 'NEEDS_REVIEW',
	CLIENT_HAS_NOT_ANSWERED = 'CLIENT_HAS_NOT_ANSWERED',
	ANSWER_SENT = 'ANSWER_SENT',
	ARCHIVED = 'ARCHIVED',
	PREAPPROVED = 'PREAPPROVED'
}
export interface Chat {
	id: string;
	customer_phone: string;
	customer_name?: string | null;
	status: ChatStatus;
	last_message_at: string | null;
	assigned_to?: string | null;
	is_from_whatsapp: boolean;
	customer_support_status: CustomerSupportStatus;
	tag: ChatTag | null;
}

export interface StatusConfig {
	color: string;
	textColor: string;
	bgColor: string;
	label: string;
}

export interface Chat {
	id: string;
	customer_phone: string;
	customer_name?: string | null;
	status: ChatStatus;
	last_message_at: string | null;
	is_from_whatsapp: boolean;
	customer_support_status: CustomerSupportStatus;
	tag: ChatTag | null;
}

export enum SenderTypes {
	AI = 'ai',
	CLIENT = 'client',
	CUSTOMER_SUPPORT = 'customer_support',
	SYSTEM = 'system',
	SCHEDULED = 'scheduled'
}

export enum TemplateStatus {
	APPROVED = 'APPROVED',
	PENDING = 'PENDING',
	REJECTED = 'REJECTED',
	FLAGGED = 'FLAGGED',
	PAUSED = 'PAUSED',
	PENDING_DELETION = 'PENDING_DELETION'
}

export enum MessageType {
	TEXT = 'text',
	IMAGE = 'image',
	VOICE = 'voice',
	INTERACTIVE = 'interactive',
	DOCUMENT = 'document'
}

export enum MessageStatus {
	SENT = 'sent',
	DELIVERED = 'delivered',
	READ = 'read',
	DELIVERY_FAILED = 'delivery_failed'
}

export interface MessageStatusFailureMetadata {
	error_code: number;
	friendly_message: string;
	timestamp: string;
}

export interface FailedStatusErrorMetadata {
	failure: MessageStatusFailureMetadata;
}

export enum InteractiveMessageTypes {
	LIST = 'list',
	BUTTON = 'buttons',
	FLOW = 'form'
}

export enum WhatsAppComponentButtonTypes {
	TEXT = 'TEXT',
	FLOW = 'FLOW',
	CATALOG = 'CATALOG'
}

export interface InteractiveMessageContentJson {
	type: InteractiveMessageTypes;
	data: Record<string, any>;
}
export interface ChatMessage {
	id: string;
	chat_id: string;
	content: string;
	is_from_customer: boolean;
	status: MessageStatus;
	sent_at: string;
	sent_by?: string;
	message_type: MessageType;
	sender_type?: SenderTypes;
	media_url?: string;
	voice_url?: string;
	metadata?: Record<string, any>;
	content_json?: InteractiveMessageContentJson;
}

export enum ChatTag {
	ORIGINACION_CAMIONETAS = 'originacion_camionetas',
	SOPORTE = 'soporte',
	ONBOARDING = 'onboarding',
	ORIGINACION_MICROCREDITO = 'originacion_microcredito',
	CONTINUAR_CONVERSACION_CREDITO = 'continuar_conversacion_credito',
	COBRANZA = 'cobranza'
}

export type TemplateName =
	| 'originacion_camionetas'
	| 'continuar_conversacion_soporte'
	| 'iniciar_conversacion_interesado'
	| 'registro_en_nueva_app'
	| 'cobranza'
	| 'recordatorio_de_correcciones'
	| 'continuar_conversacion_credito';

export const templateToTagMap: Record<TemplateName, ChatTag> = {
	originacion_camionetas: ChatTag.ORIGINACION_CAMIONETAS,
	continuar_conversacion_soporte: ChatTag.SOPORTE,
	iniciar_conversacion_interesado: ChatTag.ORIGINACION_MICROCREDITO,
	registro_en_nueva_app: ChatTag.ONBOARDING,
	recordatorio_de_correcciones: ChatTag.ONBOARDING,
	continuar_conversacion_credito: ChatTag.CONTINUAR_CONVERSACION_CREDITO,
	cobranza: ChatTag.COBRANZA
} as const;

export enum SUPABASE_BUCKET {
	WHATSAPP_IMAGES = 'whatsapp-images',
	WHATSAPP_VOICE_NOTES = 'whatsapp-voice-notes',
	WHATSAPP_DOCUMENTS = 'whatsapp-documents'
}

export type AgentTool = {
	id: string;
	name: string;
	description: string;
	category: string;
	is_active: boolean;
	created_at: string | null;
	updated_at: string | null;
};

export type ToolInstruction = {
	id: string;
	agent_id: string;
	tool_ids: string[];
	trigger_description: string;
	instruction_text: string;
	enabled: boolean;
	sort_order: number;
	created_at: string | null;
	updated_at: string | null;
};

export type ToolInstructionWithTools = ToolInstruction & {
	tools: AgentTool[];
};

export type InstructionStep = {
	type: 'tool' | 'instruction';
	toolId?: string;
	text?: string;
};

export type InstructionSequence = {
	trigger: string;
	steps: InstructionStep[];
};

export type AgentContextVariable = {
	id: string;
	name: string;
	description: string;
	data_type: 'string' | 'number' | 'boolean' | 'array' | 'object';
	is_active: boolean;
	created_at: string | null;
	updated_at: string | null;
};

export interface OrganizationAgent {
	id: string;
	organization_id: string;
	agent_type: string;
	is_active: boolean;
	system_prompt: string | null;
	model: string;
	temperature: number;
	max_tokens: number;
	transition_message: string | null;
	transition_message_where_it_happens: string | null;
	social_network_analysis_failure_message: string | null;
	metadata: Record<string, any> | null;
	created_at: string | null;
	updated_at: string | null;
}

export interface CustomerRecord {
	client_id: string;
	names: string;
	first_last_name: string;
	second_last_name?: string;
	email?: string;
	phone_number: string;
	marital_status?: string;
	state?: string;
	city?: string;
	date_of_birth?: string;
	monthly_payment?: string | number;
	credit_bureau_default_score?: string | number;
	approved_at?: string;
	curp?: string;
	started_at?: string;
	open_current_balance?: string | number;
	open_overdue_balance?: string | number;
	closed_maximum_balance?: string | number;
	total_trials?: number | string;
	months_overdue?: number | string;
	years_in_credit_bureau?: number | string;
	utilization_pre_pandemic?: string | number;
	last_six_months_inquiries?: number | string;
	utilization_post_pandemic?: string | number;
}

export interface SyncFincluCustomersRequest {
	customers: CustomerRecord[];
	send_welcome_template?: boolean;
}

export interface SyncResult {
	success: true;
	summary_id: string;
	total_processed: number;
	customers_created: number;
	customers_updated: number;
	chats_created: number;
	chats_updated: number;
	templates_sent: number;
	templates_failed: number;
	templates_skipped: number;
	templates_not_sent_reason?: string;
	failed_records: Array<{ client_id: string; error: string }>;
	failed_templates: Array<{ client_id: string; phone: string; error: string }>;
	skipped_templates: Array<{ client_id: string; phone: string; reason: string }>;
	execution_time_ms: number;
}

export interface ValidationErrorResponse {
	success: false;
	error: string;
	message: string;
	general_errors?: string[];
	customer_errors?: Array<{
		customer_index: number;
		client_id?: string;
		errors: Array<{ field: string; message: string }>;
	}>;
}

export enum CustomerProcessStatus {
	AGENT_CUSTOMER_SUPPORT = 'agent_customer_support',
	SENT_APPROVED_MESSAGE = 'sent_approved_message',
	AGENT_FILLING_QUESTIONNARE = 'agent_filling_questionnare',
	AGENT_SUGGESTING_VEHICLE = 'agent_suggesting_vehicle',
	AGENT_GATHERING_DOCUMENTS = 'agent_gathering_documents',
	AGENT_GATHERING_SOCIAL_NETWORKS = 'agent_gathering_social_networks',
	AGENT_ANALYZING_SOCIAL_NETWORKS = 'agent_analyzing_social_networks',
	AGENT_RISK_CALCULATION = 'agent_risk_calculation'
}

export interface FincluCustomer {
	id: string;
	finclu_client_id: string | null;
	whatsapp_organization_id: string;
	names: string | null;
	first_last_name: string | null;
	second_last_name: string | null;
	email: string | null;
	phone_number: string;
	marital_status: string | null;
	state: string | null;
	city: string | null;
	date_of_birth: string | null;
	credit_bureau_monthly_payment: number | null;
	credit_bureau_default_score: number | null;
	credit_bureau_open_current_balance: number | null;
	credit_bureau_open_overdue_balance: number | null;
	credit_bureau_closed_maximum_balance: number | null;
	credit_bureau_total_trials: number | null;
	credit_bureau_months_overdue: number | null;
	credit_bureau_years_in_credit_bureau: number | null;
	credit_bureau_utilization_pre_pandemic: number | null;
	credit_bureau_last_six_months_inquiries: number | null;
	credit_bureau_utilization_post_pandemic: number | null;
	approved_at: string | null;
	curp: string | null;
	created_at: string;
	updated_at: string;
	version: number;
	customer_process_status: CustomerProcessStatus;
	current_status_entered_at: string;
	fit_for_vehicular_credit: boolean | null;
	finclu_operation_status: string | null;
	finclu_operation_deliberation_history: any[] | null;
}

export interface CustomerProcessVariable {
	id: string;
	organization_id: string;
	variable_name: string;
	variable_value: number | null;
	description: string | null;
	effective_from: string;
	created_at: string;
	created_by: string | null;
	variable_type: 'text' | 'numeric';
	text_value: string | null;
}

export type VehicleOfferingType =
	| 'unable_to_pay'
	| 'offer_low_end_vehicle'
	| 'offer_high_end_vehicle';
export type VehicleType = 'fuel' | 'electric';

export interface FincluCustomerVehicleOffering {
	id: string;
	customer_id: string;
	organization_id: string;
	fuel_payment_availability_before_credit: number;
	electric_payment_availability_before_credit: number;
	vehicle_offering: VehicleOfferingType;
	vehicle_type: VehicleType | null;
	vehicle_model: string | null;
	reasoning: string;
	reasoning_spanish?: string | null;
	created_at: string;
	created_by: string;
}

export type PlatformPlan = 'A' | 'B';
export type MobilityPlatformModality = 'full_time' | 'part_time' | 'not_applicable';
export type HouseType = 'rent' | 'owned' | 'family_owned';

export interface FincluCustomerQuestionnaire {
	id: string;
	customer_id: string;
	organization_id: string;
	occupation: string | null;
	total_income: number | null;
	primary_income: number | null;
	secondary_income: number | null;
	has_mobility_platform: boolean | null;
	platform_plan: PlatformPlan | null;
	platform_plan_description: string | null;
	movility_platform_modality: MobilityPlatformModality | null;
	movility_platform_modality_description: string | null;
	rent: number | null;
	house_type: HouseType | null;
	mortgage_payment: number | null;
	family_expenses: number | null;
	partner_income: number | null;
	economic_dependants: number | null;
	economic_dependants_people_spanish: string | null;
	has_partner: boolean | null;
	wants_platform_income: boolean | null;
	platform_income_modality: string | null;
	projected_platform_income: number | null;
	fuel_consumption: number | null;
	electric_charge: number | null;
	created_at: string;
	updated_at: string;
}

export interface SocialNetworkProfileRecord {
	id: string;
	customer_id: string;
	organization_id: string;
	social_network_url: string;
	platform: 'instagram' | 'facebook' | 'tiktok' | 'linkedin' | null;
	profile_match_score: number | null;
	profile_match_reasoning: string | null;
	profile_match_reasoning_spanish?: string | null;
	bevel_reference_id: string | null;
	bevel_ai_persona_name: string | null;
	bevel_ai_persona_status: 'pending' | 'processing' | 'completed' | 'failed' | null;
	bevel_platforms: string[] | null;
	bevel_persona_data: Record<string, any> | null;
	activity_12_months: number | null;
	followers_following_ratio: number | null;
	profile_consistency: number | null;
	payment_confidence: number | null;
	arena_reference_experiment_id: string | null;
	arena_experiment_name: string | null;
	arena_experiment_status: 'pending' | 'running' | 'completed' | 'failed' | null;
	arena_interview_data: Record<string, any> | null;
	data_collection_complete: boolean;
	created_at: string;
	updated_at: string;
}

export type FuelType = 'fuel' | 'electric';
export type VehicleCategory = 'low_end' | 'high_end';

export interface OfferingVehicle {
	id: string;
	organization_id: string;
	name: string;
	minimum_weekly_payment: number;
	fuel_type: FuelType;
	category: VehicleCategory;
	description: string | null;
	is_active: boolean;
	recommendation_priority: number;
	created_at: string;
	updated_at: string;
}

export type ModalityType = 'full_time' | 'part_time' | 'not_applicable';
export type IncomeValidityType = 'low' | 'medium' | 'high';

interface WeeklySummary {
	week: number;
	income: number;
	rides: number;
	image_url: string;
}

export interface PlanAData {
	monthly_income?: number;
	monthly_drives?: number;
	platform_score?: number;
	completion_rate?: number;
	weekly_summaries?: WeeklySummary[];
	platform_score_image_url?: string;
	completion_rate_image_url?: string;
}

export interface PlanBEntry {
	monthly_income?: number;
	document_type?: 'bank_statement' | 'payslip' | 'invoice' | 'other';
	document_date?: string;
	image_url?: string;
	income_validity?: 'low' | 'medium' | 'high';
}

export interface PlanBData {
	entries?: PlanBEntry[];
	income_consistency?: 'high' | 'medium' | 'low';
}

interface ImageMetadata {
	url: string;
	type: string;
	extracted_data: Record<string, any>;
	uploaded_at: string;
}

export interface DocumentRecord {
	id: string;
	customer_id: string;
	organization_id: string;
	platform_plan: 'A' | 'B';
	plan_a_data: PlanAData;
	plan_b_data: PlanBData;
	verification_status: 'pending' | 'complete' | 'needs_review';
	images_metadata: ImageMetadata[];
	created_at: string;
	updated_at: string;
}
